const get = require('lodash/get')
const { proposeChange } = require('./propose-change')
const { getByIds } = require('./../../api/read/get-by-ids')
const { listWarehouseCodes } = require('./../read/list-warehouse-codes')
const { list: listPrograms } = require('../../../program')

const fillNonUserPrograms = (programsFromForm, programsFromLocation, userPrograms) => {
  const nonUserPrograms = programsFromLocation.filter(lP => {
    return !userPrograms.find(uP => lP.id === uP.id)
  })

  return programsFromForm.concat(nonUserPrograms)
}

exports.saveLocations = async (state, bulkLocations, date, userPrograms, supportCountryInId, { autoGenerateWarehouseCodes } = {}) => {
  let docs = bulkLocations.filter(doc => !Object.keys(doc.errorDoc).length)
  let createCount = 0
  let updateCount = 0
  if (!docs.length) return { createCount, updateCount }

  const ngStateId = ({ zone, state }) => {
    if (zone && state) return `zone:${zone}:state:${state}`
  }

  const existingCodes = {}
  if (autoGenerateWarehouseCodes) {
    // Get all warehouse codes with a prefix based on the zone and
    // state of the first row of the uploaded sheet.
    // If `existingCodes` are not passed `proposeChange`
    // will fetch them by calling `listWarehouseCodes` for each single new
    // location. Same will happen for every single new
    // location that belongs to a different zone and state.
    // This is an optimization that makes sense only when creating new
    // Nigerian locations and specially if all locations belong to the
    // same state, like in the case of the new single state Nigerian tenants.
    const firstStateId = ngStateId(docs[0].changes.location)
    if (firstStateId) {
      existingCodes[firstStateId] = await listWarehouseCodes(state, docs[0].changes)
    }
  }

  // Likewise we fetch the program here so that `listWarehouseCodes`
  // does not need to fetch them for each single location.
  const programs = await listPrograms(state, { unfiltered: true })

  let allDocs = []
  const batchSize = 500
  for (let i = 0; i < docs.length; i += batchSize) {
    const uploadBatch = docs.slice(i, i + batchSize)
    let existingLocationDocs = await getByIds(state, uploadBatch.map(({changes: doc}) => doc._id), date)
    existingLocationDocs = existingLocationDocs.filter(location => location)

    const promises = []
    for (const entity of uploadBatch) {
      let {
        changes: doc,
        row
      } = entity

      try {
        const locationId = doc._id

        const existingDoc = existingLocationDocs.find(doc => doc._id === locationId)

        if (existingDoc) {
          const programs = fillNonUserPrograms(doc.programs, existingDoc.programs, userPrograms)
          doc.programs = programs

          const changeProposal = {
            edits: doc,
            effectiveDate: date,
            proposalType: 'update',
            usePSMCodes: true
          }
          updateCount++
          promises.push(proposeChange(state, changeProposal))
        } else {
          // These ones need to run in sequence to avoid race conditions
          // and we need to update existingCodes after every single creation.
          const changeProposal = {
            edits: doc,
            effectiveDate: date,
            proposalType: 'create',
            usePSMCodes: true,
            autoGeneratePSMCodes: autoGenerateWarehouseCodes,
            existingCodes, // only used if usePSMCodes and autoGeneratePSMCodes are true
            programs,
            supportCountry: supportCountryInId
          }
          const result = await proposeChange(state, changeProposal)
          // If we're auto generating codes and this is the state for which we
          // originally fetched the warehouse codes, add the new one.
          if (autoGenerateWarehouseCodes) {
            const created = result.currentLocation
            const stateId = ngStateId(created.location)
            if (existingCodes[stateId]) {
              const warehouseCode = get(created, 'additionalData.warehouseCode')
              existingCodes[stateId].push(warehouseCode)
            }
          }
          createCount++
          promises.push(result)
        }
      } catch (e) {
        let message = get(e, 'message', e)
        if (e.status === 409) {
          message = 'Location already exists. Remember to include an _id column for location updates.'
        }
        const error = {
          row,
          fullName: doc.fullName,
          errorDoc: [`${message}`]
        }
        promises.push(error)
      }
    }
    const savedDocs = await Promise.all(promises)
    allDocs = allDocs.concat(savedDocs)
  }

  return {
    allDocs,
    createCount,
    updateCount
  }
}
